/**
 * 路由权限配置
 * Route Permission Configuration
 * 
 * 集中管理所有路由和对应的权限要求
 */

export interface RouteConfig {
  path: string;
  permission?: string | string[] | null;  // 所需权限，null 表示所有人可访问
  children?: RouteConfig[];
}

/**
 * 路由权限配置表
 * 
 * 权限格式: resource:action
 * - null: 所有登录用户可访问
 * - string: 需要单个权限
 * - string[]: 需要任意一个权限（OR 关系）
 */
export const routeConfigs: RouteConfig[] = [
  // 公共页面
  { path: '/', permission: null },
  { path: '/dashboard', permission: null },
  
  // 项目管理
  { path: '/project', permission: 'project:view' },
  { path: '/project-control-center', permission: 'project:view' },
  
  // 工时记录
  { path: '/worktime', permission: 'worktime:view' },

  // 工时批量提交历史
  { path: '/worktime-batch-history', permission: 'worktime:view' },
  
  // 设备使用
  { path: '/equipment', permission: 'equipment:view' },
  
  // 材料使用
  { path: '/material', permission: 'material:view' },
  
  // 识别数据审核
  { path: '/recognition-review', permission: 'material:view' },
  
  // 库存管理
  { path: '/inventory', permission: 'inventory:view' },
  
  // 材料调拨
  { path: '/transfer', permission: 'transfer:view' },
  
  // 进度跟踪模块
  { path: '/progress/work-items', permission: 'progress:view' },
  { path: '/progress/records', permission: 'progress:view' },
  { path: '/progress/additions', permission: 'progress:view' },
  { path: '/progress/rectifications', permission: 'progress:view' },
  { path: '/progress/reports/progress', permission: 'report:view' },
  { path: '/progress/reports/additions', permission: 'report:view' },
  { path: '/progress/reports/rectifications', permission: 'report:view' },
  
  // 报表分析（旧版）
  { path: '/reports', permission: 'report:view' },
  
  // 新报表中心 - 完整权限配置
  { path: '/reports-new', permission: ['report:view', 'report:manage'] },
  { path: '/reports-new/dashboard', permission: ['report:dashboard:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/cost', permission: ['report:cost:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/progress', permission: ['report:progress:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/material', permission: ['report:material:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/worktime', permission: ['report:worktime:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/inventory', permission: ['report:inventory:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/equipment', permission: ['report:equipment:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/change-order', permission: ['report:changeorder:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/rectification', permission: ['report:rectification:view', 'report:view', 'report:manage'] },
  { path: '/reports-new/transfer', permission: ['report:transfer:view', 'report:view', 'report:manage'] },
  
  // 系统管理（需要管理员权限）
  { path: '/data-management', permission: 'system:manage' },
  { path: '/data-validation', permission: 'system:manage' },
  { path: '/price-config', permission: 'system:manage' },
  { path: '/user-management', permission: 'system:manage' },
  { path: '/user-permission-manage', permission: 'system:manage' },
  { path: '/permission-matrix', permission: 'system:manage' },
  
  // 供应商管理
  { path: '/supplier-management', permission: 'supplier:manage' },
  { path: '/equipment-provider-management', permission: 'supplier:manage' },
  { path: '/subcontractor-management', permission: 'supplier:manage' },
  { path: '/subcontract-cost-management', permission: 'project:view' },
  { path: '/coordination-cost-management', permission: 'project:view' },

  // 工人管理
  { path: '/worker-manage', permission: 'worker:view' },
  { path: '/worker-history', permission: 'worker:view' },

  // 材料识别与批量录入
  { path: '/recognition-management', permission: 'material:view' },
  { path: '/batch-material-parse', permission: 'material:create' },
  { path: '/batch-semantic-parse', permission: 'worktime:create' },

  // 进度子路由
  { path: '/progress/templates', permission: 'progress:view' },

  // 测试页面
  { path: '/responsive-test', permission: null },
  { path: '/sync-demo', permission: null },
  
  // 权限拒绝页面
  { path: '/permission-denied', permission: null },
];

/**
 * 根据路径查找路由配置
 */
export function findRouteConfig(path: string): RouteConfig | undefined {
  // 精确匹配
  let config = routeConfigs.find(r => r.path === path);
  if (config) return config;
  
  // 前缀匹配（用于子路由）
  // 例如：/progress/additions/123 匹配 /progress/additions
  const sortedConfigs = [...routeConfigs].sort((a, b) => b.path.length - a.path.length);
  config = sortedConfigs.find(r => path.startsWith(r.path));
  
  return config;
}

/**
 * 检查用户是否有权限访问指定路由
 */
export function checkRoutePermission(
  path: string,
  userPermissions: string[]
): boolean {
  const config = findRouteConfig(path);
  
  // 路由不存在时默认放行，避免前端新增页面但遗漏配置时直接被拦截
  // 真正的数据安全仍由后端接口权限控制
  if (!config) {
    console.warn(`[RouteConfig] 路由配置不存在，默认允许访问: ${path}`);
    return true;
  }
  
  // 没有权限要求，允许访问
  if (config.permission === null || config.permission === undefined) {
    return true;
  }
  
  // system:manage 拥有全局访问能力
  if (userPermissions.includes('system:manage')) {
    return true;
  }

  const hasPermission = (permission: string): boolean => {
    if (userPermissions.includes(permission)) {
      return true;
    }

    const parts = permission.split(':');
    const resource = parts[0];
    const subResource = parts.length === 3 ? parts[1] : null;

    if (subResource && userPermissions.includes(`${resource}:${subResource}:manage`)) {
      return true;
    }

    if (resource && userPermissions.includes(`${resource}:manage`)) {
      return true;
    }

    return false;
  };

  // 单个权限
  if (typeof config.permission === 'string') {
    return hasPermission(config.permission);
  }
  
  // 多个权限（OR 关系）
  if (Array.isArray(config.permission)) {
    return config.permission.some(hasPermission);
  }
  
  return false;
}

